import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useAppSelector } from "@/reducers/hooks"
import { selectRoutingProvider } from "@/reducers/serviceConfigSelectors"
import { useDarkMode } from "@/contexts/DarkModeContext"
import {
  GeoJSONSource,
  Map as MapLibreMap,
  PointLike,
  Popup,
} from "maplibre-gl"
import polyline from "@mapbox/polyline"
import AdHocCommands from "@/api/adHocCommands"
import RoutePolylinesParser from "@/lib/routePolylinesPareser"
import "maplibre-theme/icons.default.css"
import "maplibre-theme/classic.css"

import ChangeMapStyle from "../maps/ChangeMapStyle"
import { DriverDisplayInfo } from "@/reducers/accountChatsSelectors"
import MapManager, { MapStyle } from "@/components/maps/MapManager"
import { Button } from "@blueprintjs/core"
import CloseMobileMap from "@/components/maps/CloseMobileMap"
import { isMobile } from "@/utils/detectDevice"
import DriverInfo from "@/components/fleetOverview/driver-info"
import { CAR_SPEED_FLAG, carSpeedLabel } from "@/lib/carSpeed"
import { renderToString } from "react-dom/server"
import { Card } from "../ui/card"
import { Route2g } from "@/reducers/routes2gSlice"
import { useMapToaster } from "@/hooks/useMapToaster"
import { TrailerLocation } from "@/reducers/trailerLocationsSlice"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"
import { getIntlLocale } from "@/lib/i18n/format"

// Trailers are a plain symbol layer, not per-marker popups like vehicles:
// nothing to hover, select, open a chat for or draw a route to.
const TRAILERS_SOURCE_ID = "trailers-source"
const TRAILERS_LAYER_ID = "trailers-layer"
const TRAILERS_ARROW_LAYER_ID = "trailers-arrow-layer"
const TRAILERS_LABEL_LAYER_ID = "trailers-label-layer"
const TRAILER_ARROW_IMAGE_ID = "trailer-arrow"
// Both marker layers take clicks and hover; the label layer does not.
const TRAILER_HIT_LAYER_IDS = [TRAILERS_LAYER_ID, TRAILERS_ARROW_LAYER_ID]

type TrailerPopupProperties = { label?: string; odometer?: number | null }

const createTrailersGeoJSON = (trailers: TrailerLocation[]) =>
  ({
    type: "FeatureCollection",
    features: trailers
      .filter((trailer) => trailer.location?.lat && trailer.location?.lon)
      .map((trailer) => {
        // Webfleet only reports a course while the trailer moves, so a
        // heading doubles as "moving": arrow when known, plain dot otherwise.
        const bearing = parseFloat(trailer.location?.bearing ?? "")
        const hasHeading = Number.isFinite(bearing)
        return {
          type: "Feature",
          properties: {
            id: trailer.id,
            label: trailer.label || "",
            odometer: trailer.location?.odometer ?? null,
            bearing: hasHeading ? bearing : null,
            hasHeading,
          },
          geometry: {
            type: "Point",
            coordinates: [
              parseFloat(trailer.location!.lon!),
              parseFloat(trailer.location!.lat!),
            ],
          },
        }
      }),
  }) as GeoJSON.FeatureCollection

// Drawn on a canvas rather than loaded from an SVG so the image is ready the
// moment the layer is added (MapManager's svgToCanvas loads asynchronously).
// Points north; the layer rotates it by the trailer's bearing.
const TRAILER_ARROW_SIZE = 36 // device px, shown at pixelRatio 2
const trailerArrowImage = (): ImageData => {
  const size = TRAILER_ARROW_SIZE
  const canvas = document.createElement("canvas")
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext("2d")!
  const middle = size / 2
  ctx.beginPath()
  ctx.moveTo(middle, 3)
  ctx.lineTo(size - 5, size - 5)
  ctx.lineTo(middle, size - 12)
  ctx.lineTo(5, size - 5)
  ctx.closePath()
  ctx.fillStyle = "#f59e0b"
  ctx.fill()
  ctx.lineWidth = 3
  ctx.lineJoin = "round"
  ctx.strokeStyle = "#ffffff"
  ctx.stroke()
  return ctx.getImageData(0, 0, size, size)
}

const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ] || char,
  )

const trailerPopupHTML = (properties: TrailerPopupProperties) => {
  const odometer =
    typeof properties.odometer === "number"
      ? `<div class="text-xs text-gray-500 dark:text-gray-400">${Math.round(properties.odometer).toLocaleString(getIntlLocale())} km</div>`
      : ""
  // The global dark theme repaints .maplibregl-popup-content, so the text
  // has to follow it too.
  return `
    <div class="trailer-popup px-3 py-2 text-gray-900 dark:text-gray-100">
      <div class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">${escapeHtml(i18n.t("fleet.fleetOverview.trailer"))}</div>
      <div class="text-sm font-semibold">${escapeHtml(properties.label || i18n.t("fleet.fleetOverview.unnamedTrailer"))}</div>
      ${odometer}
    </div>
  `
}

export type FleetOverviewMapRef = {
  showDriverPopup: (driver: DriverDisplayInfo) => void
  flyToDriver: (driver: DriverDisplayInfo, zoomLevel?: number) => void
  showTrailerPopup: (trailer: TrailerLocation) => void
  flyToTrailer: (trailer: TrailerLocation, zoomLevel?: number) => void
}

type FleetOverviewMapProps = {
  drivers: DriverDisplayInfo[]
  trailers: TrailerLocation[]
  onClose: () => void
  selectedDriver: DriverDisplayInfo | null
  hoveredDriver: string | null
  onDriverSelect?: (driver: DriverDisplayInfo | null) => void
  onHoverDriver?: (jid: string | null) => void
  onNavigateToChat: (jid: string, shouldJoin: boolean) => void
  onCloseFilter: () => void
}

const FleetOverviewMap = forwardRef<FleetOverviewMapRef, FleetOverviewMapProps>(
  (
    {
      drivers,
      trailers,
      selectedDriver,
      hoveredDriver,
      onClose,
      onDriverSelect,
      onHoverDriver,
      onNavigateToChat,
      onCloseFilter,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [currentMapStyle, setCurrentMapStyle] = useState<MapStyle>("default")
    const popups = useRef<Record<string, Popup>>({})
    const persistentPopups = useRef<Record<string, Popup>>({})
    // Text each marker was built with. Markers are created once per driver,
    // so a changed text (a car or flag that loaded late) means rebuilding it.
    const persistentPopupTexts = useRef<Record<string, string>>({})
    // Fleets that know their vans by side number ask for "47 SPRINTER" on the
    // marker instead of the plate (Bodex, 2026-10-22), or "2 CW4129C" when
    // only the chat name carries the side number (euro-transchem, 2026-11-03).
    const showCarLabel = Boolean(useFeatureFlagEnabled("fleet-map-car-label"))
    const showSpeed = Boolean(useFeatureFlagEnabled(CAR_SPEED_FLAG))
    // Marker handlers keep the popup builder they were made with, and the
    // flag can arrive after them, so the builder reads it through a ref.
    const showSpeedRef = useRef(showSpeed)
    showSpeedRef.current = showSpeed
    const persistentPopupHandlers = useRef<
      Record<
        string,
        {
          mouseenterHandler: () => void
          mouseleaveHandler: () => void
          clickHandler: (e: Event) => void
          wheelHandler: (e: WheelEvent) => void
          platePopupElement: Element | null
        }
      >
    >({})
    const [visiblePopupDriverJid, setVisiblePopupDriverJid] = useState<
      string | null
    >(null)
    const visibleRouteDriverJidRef = useRef<string | null>(null)
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const mapManagerRef = useRef<MapManager | null>(null)
    const lastPopupCloseTimeRef = useRef<number>(0)
    // Latest trailers for the layer builders; they run from timeouts and
    // style-change callbacks, where a closure would see a stale array.
    const trailersRef = useRef<TrailerLocation[]>(trailers)
    trailersRef.current = trailers
    // Latest driver state by jid for the plate-popup listeners. The plate
    // popups are created once per driver and their click handler closes over
    // the driver as it was then, so a tab left open showed yesterday's route
    // in the detail popup (fracht DWR9387T, 2026-09-30: 617 km and an ETA
    // from the previous afternoon while the list already said 303 km).
    const driversByJidRef = useRef<Record<string, DriverDisplayInfo>>({})
    driversByJidRef.current = Object.fromEntries(
      drivers.map((driver) => [driver.jid, driver]),
    )
    const trailerPopupRef = useRef<Popup | null>(null)
    // changeMapStyle builds a brand-new map instance, and layer-scoped
    // listeners die with the old one, so remember which instance has them.
    const trailerHandlersMapRef = useRef<MapLibreMap | null>(null)

    // Track all timeouts for cleanup
    const timeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set())

    const { isDarkMode } = useDarkMode()
    const routingProvider = useAppSelector(selectRoutingProvider)

    const handleZoom = (e: WheelEvent) => {
      mapManagerRef.current?.getMapInstance()?.scrollZoom.wheel(e)
    }

    const drawOnceMapIsLoaded = useCallback(
      (drawFunction: () => void, layerId: string) => {
        if (!mapManagerRef.current) return

        let attemptCount = 0
        const maxAttempts = 20 // Increased max attempts

        const attemptDraw = () => {
          try {
            const mapInstance = mapManagerRef.current?.getMapInstance()

            // Check if map style is loaded and map is ready
            if (
              mapInstance &&
              mapInstance.isStyleLoaded() &&
              mapInstance.loaded()
            ) {
              drawFunction()
              return
            }

            // Stop trying after max attempts
            attemptCount++
            if (attemptCount >= maxAttempts) {
              console.warn(
                `Failed to draw ${layerId} after ${maxAttempts} attempts - map style not loaded`,
              )
              return
            }

            // If style is not loaded, schedule next attempt
            const delay = attemptCount < 5 ? 100 : 200 // Shorter delay initially
            const timeoutId = setTimeout(() => {
              timeoutsRef.current.delete(timeoutId)
              attemptDraw()
            }, delay)

            timeoutsRef.current.add(timeoutId)
          } catch (err) {
            console.error("Error in drawOnceMapIsLoaded:", err)
            // Try again after error
            attemptCount++
            if (attemptCount < maxAttempts) {
              const timeoutId = setTimeout(() => {
                timeoutsRef.current.delete(timeoutId)
                attemptDraw()
              }, 500)
              timeoutsRef.current.add(timeoutId)
            }
          }
        }

        attemptDraw()
      },
      [mapManagerRef],
    )

    const removeRouteFromMap = useCallback((driverJid: string) => {
      const mapInstance = mapManagerRef.current?.getMapInstance()
      if (!mapInstance) return

      const routeSourceId = `route-source-${driverJid}`
      const routeLayerId = `route-layer-${driverJid}`
      const routeStopsSourceId = `route-stops-source-${driverJid}`
      const routeStopsLayerId = `route-stops-layer-${driverJid}`
      const routeStopsIconSourceId = `route-stops-icons-source-${driverJid}`
      const routeStopsIconLayerId = `route-stops-icons-layer-${driverJid}`

      // Remove layers and sources
      if (mapInstance.getLayer(routeLayerId)) {
        mapInstance.removeLayer(routeLayerId)
      }
      if (mapInstance.getSource(routeSourceId)) {
        mapInstance.removeSource(routeSourceId)
      }
      if (mapInstance.getLayer(routeStopsLayerId)) {
        mapInstance.removeLayer(routeStopsLayerId)
      }
      if (mapInstance.getSource(routeStopsSourceId)) {
        mapInstance.removeSource(routeStopsSourceId)
      }
      if (mapInstance.getLayer(routeStopsIconLayerId)) {
        mapInstance.removeLayer(routeStopsIconLayerId)
      }
      if (mapInstance.getSource(routeStopsIconSourceId)) {
        mapInstance.removeSource(routeStopsIconSourceId)
      }
    }, [])

    useEffect(() => {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          // First priority: close driver detail popup if one is visible
          if (visiblePopupDriverJid) {
            // Close the detail popup
            Object.values(popups.current).forEach((popup) => popup.remove())
            popups.current = {}
            setVisiblePopupDriverJid(null)
            onDriverSelect?.(null)

            // Also hide route if visible
            if (visibleRouteDriverJidRef.current) {
              removeRouteFromMap(visibleRouteDriverJidRef.current)
              visibleRouteDriverJidRef.current = null
            }
            return
          }
          onCloseFilter()
        }
      }

      window.addEventListener("keydown", handleEscKey)

      return () => {
        window.removeEventListener("keydown", handleEscKey)
      }
    }, [
      onClose,
      visiblePopupDriverJid,
      onDriverSelect,
      removeRouteFromMap,
      onCloseFilter,
    ])

    const createCarsGeoJSON = useCallback(
      (driversData: DriverDisplayInfo[]) => {
        return {
          type: "FeatureCollection",
          features: driversData
            .filter((driver) => driver.location?.lat && driver.location?.lon)
            .map((driver) => {
              return {
                type: "Feature",
                properties: {
                  id: driver.jid,
                  name: driver.profile?.name || "Unknown Driver",
                  phone: driver.profile?.phone || "No phone",
                  plates: driver.plates || "",
                  jid: driver.jid,
                  isDriving: driver.isDriving,
                  isHovered: hoveredDriver === driver.jid,
                  unreadCount: driver.unreadCount,
                },
                geometry: {
                  type: "Point",
                  coordinates: [
                    parseFloat(driver.location.lon!),
                    parseFloat(driver.location.lat!),
                  ],
                },
              }
            }),
        } as unknown as GeoJSON.FeatureCollection
      },
      [hoveredDriver],
    )

    const createDriverDetailHTML = useCallback((driver: DriverDisplayInfo) => {
      return renderToString(
        <Card className="mx-auto w-full max-w-xl overflow-hidden rounded-lg bg-card shadow-md">
          <DriverInfo
            {...driver}
            speedLabel={
              showSpeedRef.current ? carSpeedLabel(driver.location) : undefined
            }
            onClose={() => {}}
            onNavigateToChat={() => {}}
            onFlyToLocation={() => {}}
          />
        </Card>,
      )
    }, [])

    // The detail popup is static HTML with listeners on its buttons, so a new
    // position reaches its speed line by patching the node, not re-rendering.
    useEffect(() => {
      Object.entries(popups.current).forEach(([jid, popup]) => {
        const line = popup.getElement()?.querySelector("[data-car-speed]")
        const driver = drivers.find((candidate) => candidate.jid === jid)
        if (!line || !driver) return
        const label = showSpeed ? carSpeedLabel(driver.location) : undefined
        line.classList.toggle("hidden", !label)
        const text = line.querySelector("[data-car-speed-label]")
        if (text) text.textContent = label ?? ""
      })
    }, [drivers, showSpeed])

    const addRouteToMap = useCallback(
      (
        driverJid: string,
        polylines: string[],
        routeStops?: Route2g["stops"],
      ) => {
        const mapInstance = mapManagerRef.current?.getMapInstance()
        if (!mapInstance) {
          return
        }

        // Decode polylines and create GeoJSON
        const coordinates = polylines.flatMap((poly) =>
          polyline.decode(poly).map(([lat, lng]) => [lng, lat]),
        )

        if (coordinates.length === 0) {
          return
        }

        const lats = coordinates.map((coord) => coord[1])
        const lngs = coordinates.map((coord) => coord[0])
        const bounds = {
          minLat: Math.min(...lats),
          maxLat: Math.max(...lats),
          minLng: Math.min(...lngs),
          maxLng: Math.max(...lngs),
        }

        const routeSourceId = `route-source-${driverJid}`
        const routeLayerId = `route-layer-${driverJid}`

        // Add route line
        const addLayersWhenReady = () => {
          if (!mapInstance.isStyleLoaded()) {
            setTimeout(addLayersWhenReady, 100)
            return
          }

          if (!mapInstance.getSource(routeSourceId)) {
            mapInstance.addSource(routeSourceId, {
              type: "geojson",
              data: {
                type: "Feature",
                properties: {},
                geometry: {
                  type: "LineString",
                  coordinates,
                },
              },
            })

            mapInstance.addLayer({
              id: routeLayerId,
              type: "line",
              source: routeSourceId,
              layout: {
                "line-join": "round",
                "line-cap": "round",
              },
              paint: {
                "line-color": "#4285F4", // Google Blue
                "line-width": 4,
                "line-opacity": 1.0,
              },
            })
          }

          // Add route stops with action icons
          const routeStopsIconSourceId = `route-stops-icons-source-${driverJid}`
          const routeStopsIconLayerId = `route-stops-icons-layer-${driverJid}`

          if (!mapInstance.getSource(routeStopsIconSourceId)) {
            let stopsFeatures: any = [] as any

            // If we have driver stops data, use it
            if (routeStops && routeStops.length > 0) {
              stopsFeatures = routeStops
                .filter((stop) => {
                  return stop.location?.lat && stop.location?.lon
                })
                .map((stop) => {
                  return {
                    type: "Feature",
                    properties: {
                      action: stop.action || "other",
                      name: stop.location?.formattedAddress || "Stop",
                    },
                    geometry: {
                      type: "Point",
                      coordinates: [
                        parseFloat(stop.location.lon!),
                        parseFloat(stop.location.lat!),
                      ],
                    },
                  }
                })
            }
            // Only create the layer if we have features to show
            if (stopsFeatures.length > 0) {
              mapInstance.addSource(routeStopsIconSourceId, {
                type: "geojson",
                data: {
                  type: "FeatureCollection",
                  features: stopsFeatures,
                },
              })

              mapInstance.addLayer({
                id: routeStopsIconLayerId,
                type: "symbol",
                source: routeStopsIconSourceId,
                layout: {
                  "icon-image": [
                    "case",
                    ["==", ["get", "action"], "delivery"],
                    "lucide-delivery",
                    ["==", ["get", "action"], "pickup"],
                    "lucide-pickup",
                    ["==", ["get", "action"], "refuel"],
                    "lucide-refuel",
                    ["==", ["get", "action"], "parking"],
                    "lucide-parking",
                    ["==", ["get", "action"], "customs"],
                    "lucide-customs",
                    ["==", ["get", "action"], "ferry"],
                    "lucide-ferry",
                    ["==", ["get", "action"], "truck"],
                    "lucide-truck",
                    ["==", ["get", "action"], "waypoint"],
                    "lucide-waypoint",
                    ["==", ["get", "action"], "passthrough"],
                    "lucide-passthrough",
                    ["==", ["get", "action"], "scale"],
                    "lucide-scale",
                    ["==", ["get", "action"], "drop-off"],
                    "lucide-arrow-down-to-line",
                    "lucide-other",
                  ],
                  "icon-size": 0.8,
                  "icon-allow-overlap": true,
                  "icon-ignore-placement": true,
                },
              })
            }
          }

          // Zoom to route bounds
          if (coordinates.length > 0) {
            mapInstance.fitBounds(
              [
                [bounds.minLng, bounds.minLat],
                [bounds.maxLng, bounds.maxLat],
              ],
              {
                padding: 50,
                duration: 1000,
              },
            )
          }
        }

        // Start the process
        addLayersWhenReady()
      },
      [],
    )

    const handleShowRoute = useCallback(
      async (driver: DriverDisplayInfo) => {
        const mapInstance = mapManagerRef.current?.getMapInstance()
        if (!mapInstance || !window.XMPP) return
        const routeJid = driver.routeJid
        if (!routeJid) return

        // If route is already visible for this driver, hide it
        if (visibleRouteDriverJidRef.current === routeJid) {
          removeRouteFromMap(routeJid)
          visibleRouteDriverJidRef.current = null
          return
        }

        // Remove any existing route before showing new one
        if (visibleRouteDriverJidRef.current) {
          removeRouteFromMap(visibleRouteDriverJidRef.current)
        }

        try {
          // Fetch route polylines for the driver
          const result = await AdHocCommands.getRoutePolylines(
            window.XMPP,
            routeJid,
          )
          const routeInfo = RoutePolylinesParser.parse(result)?.route

          if (routeInfo && routeInfo.routes && routeInfo.routes[0]) {
            const polylines = routeInfo.routes[0].legs.map(
              ({ polyline: poly }: { polyline: string }) => poly,
            )
            // Use driver stops instead of route stops
            addRouteToMap(routeJid, polylines, driver.stops)
            visibleRouteDriverJidRef.current = routeJid
          } else {
            console.warn("No route data found:", { routeInfo })
          }
        } catch (error) {
          console.error("Failed to fetch route for driver:", routeJid, error)
        }
      },
      [removeRouteFromMap, addRouteToMap],
    )

    const cleanupPersistentPopup = useCallback((jid: string) => {
      // Remove event handlers first
      const handlers = persistentPopupHandlers.current[jid]
      if (handlers && handlers.platePopupElement) {
        handlers.platePopupElement.removeEventListener(
          "mouseenter",
          handlers.mouseenterHandler,
        )
        handlers.platePopupElement.removeEventListener(
          "mouseleave",
          handlers.mouseleaveHandler,
        )
        handlers.platePopupElement.removeEventListener(
          "click",
          handlers.clickHandler,
        )
        // @ts-ignore
        handlers.platePopupElement.removeEventListener(
          "wheel",
          handlers.wheelHandler,
        )
        delete persistentPopupHandlers.current[jid]
      }

      // Remove popup from map
      const popup = persistentPopups.current[jid]
      if (popup) {
        popup.remove()
        delete persistentPopups.current[jid]
      }
      delete persistentPopupTexts.current[jid]
    }, [])

    const createPersistentPopups = useCallback(() => {
      const mapInstance = mapManagerRef.current?.getMapInstance()
      if (!mapInstance) return

      // Only create popups if they don't exist
      drivers.forEach((driver) => {
        if (!driver.location?.lat || !driver.location?.lon) return

        const markerText =
          (showCarLabel && driver.carLabel) ||
          driver.plates ||
          t("fleet.fleetOverview.noPlates")
        if (persistentPopups.current[driver.jid]) {
          // Skip if already exists, unless what it says has changed
          if (persistentPopupTexts.current[driver.jid] === markerText) return
          cleanupPersistentPopup(driver.jid)
        }

        const coordinates: [number, number] = [
          parseFloat(driver.location.lon),
          parseFloat(driver.location.lat),
        ]

        const offRouteIndicator = driver.isOffRoute
          ? '<div class="relative w-4 h-4 text-white bg-destructive rounded-full flex items-center justify-center">\n' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-alert-icon lucide-circle-alert"><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>' +
            "</div>"
          : ""

        const drivingIndicator = driver.isDriving
          ? '<div class="relative w-4 h-4 bg-green-700 rounded-full flex items-center justify-center">\n' +
            '  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevrons-right">\n' +
            '    <path d="m6 17 5-5-5-5"/>\n' +
            '    <path d="m13 17 5-5-5-5"/>\n' +
            "  </svg>\n" +
            "</div>"
          : ""

        let backgroundColor = "bg-violet-200"
        if (driver.showAlert) {
          if (driver.isNextStopLate) {
            backgroundColor = "bg-red-200"
          } else {
            backgroundColor = "bg-green-200"
          }
        } else if (driver.nextStop) {
          backgroundColor = "bg-gray-200"
        }

        const popupHTML = `
        <div class="plate-popup text-black dark:text-black ${backgroundColor} transition-all duration-300 [&.selected]:scale-125 [&.selected]:z-1000 border-transparent border-2  [&.hovered]:border-black  [&.hovered]:border-2 [&.selected]:border-black  [&.selected]:border-2 cursor-pointer px-2 py-1 text-xs font-medium rounded-lg shadow-md  text-secondary-foreground">
          <div class="flex items-center gap-1">
            ${drivingIndicator}
            ${offRouteIndicator}
            <span>${escapeHtml(markerText)}</span>
          </div>
        </div>
      `
        const popup = new Popup({
          closeButton: false,
          closeOnClick: false,
          closeOnMove: false,
          offset: [0, -15],
          className: `persistent-popup`,
          anchor: "bottom",
        })
          .setLngLat(coordinates)
          .setHTML(popupHTML)
          .addTo(mapInstance)

        // Add data attribute for identification and click handler
        setTimeout(() => {
          const popupElement = popup.getElement()
          if (popupElement) {
            popupElement.setAttribute("data-driver-jid", driver.jid)

            // Add click handler to the plate popup
            const platePopup = popupElement.querySelector(".plate-popup")
            if (platePopup) {
              // Create handler functions to store references
              const mouseenterHandler = () => {
                onHoverDriver?.(driver.jid)
              }

              const mouseleaveHandler = () => {
                onHoverDriver?.(null)
              }

              const clickHandler = (e: Event) => {
                e.stopPropagation()

                // Check if we're in a cooldown period after closing a popup
                const timeSinceClose =
                  Date.now() - lastPopupCloseTimeRef.current
                if (timeSinceClose < 300) {
                  // 300ms cooldown
                  return
                }

                // The listener is registered once per plate popup, so read
                // the driver's current state instead of the snapshot captured
                // when the popup was created.
                const currentDriver =
                  driversByJidRef.current[driver.jid] ?? driver

                // Always select the driver when clicked
                onDriverSelect?.(currentDriver)

                // Only show popup if it's not already visible for this driver
                if (visiblePopupDriverJid !== driver.jid) {
                  showDriverDetailPopup(currentDriver)
                }
              }

              const wheelHandler = (e: WheelEvent) => {
                handleZoom(e)
              }

              // Add event listeners
              platePopup.addEventListener("mouseenter", mouseenterHandler)
              platePopup.addEventListener("mouseleave", mouseleaveHandler)
              platePopup.addEventListener("click", clickHandler)
              // @ts-ignore
              platePopup.addEventListener("wheel", wheelHandler)

              // Store handlers for cleanup
              persistentPopupHandlers.current[driver.jid] = {
                mouseenterHandler,
                mouseleaveHandler,
                clickHandler,
                wheelHandler,
                platePopupElement: platePopup,
              }
            }
          }
        }, 0)

        persistentPopups.current[driver.jid] = popup
        persistentPopupTexts.current[driver.jid] = markerText
      })
    }, [
      drivers,
      onHoverDriver,
      onDriverSelect,
      visiblePopupDriverJid,
      showCarLabel,
      cleanupPersistentPopup,
      t,
    ])

    const updatePopupClasses = useCallback(() => {
      drivers.forEach((driver) => {
        const popup = persistentPopups.current[driver.jid]
        const popupElement = document.querySelector(
          `[data-driver-jid="${driver.jid}"] .plate-popup`,
        )
        if (!popupElement || !popup) return

        const isHovered = hoveredDriver === driver.jid
        const isSelected = selectedDriver?.jid === driver.jid
        // Remove existing state classes
        popupElement.classList.remove("hovered", "selected")

        // Update z-index on the popup container element
        const popupContainer = popup.getElement()
        if (popupContainer) {
          if (isHovered || isSelected) {
            popupContainer.style.zIndex = "1000"
          } else {
            popupContainer.style.zIndex = ""
          }
        }

        // Add appropriate state class
        if (isHovered) {
          popupElement.classList.add("hovered")
        }
        if (isSelected) {
          popupElement.classList.add("selected")
        }
      })
    }, [drivers, hoveredDriver, selectedDriver])

    const showDriverDetailPopup = useCallback(
      (driver: DriverDisplayInfo) => {
        const mapInstance = mapManagerRef.current?.getMapInstance()
        if (!mapInstance || !driver.location?.lat || !driver.location?.lon)
          return

        // Close any existing detail popups
        Object.values(popups.current).forEach((popup) => popup.remove())
        popups.current = {}

        if (visibleRouteDriverJidRef.current) {
          removeRouteFromMap(visibleRouteDriverJidRef.current)
          visibleRouteDriverJidRef.current = null
        }

        // Clear any existing visible popup state
        setVisiblePopupDriverJid(null)

        const coordinates: [number, number] = [
          parseFloat(driver.location.lon),
          parseFloat(driver.location.lat),
        ]

        // Get the plate popup element
        const platePopup = persistentPopups.current[driver.jid]
        const popupElement = document.querySelector(
          `[data-driver-jid="${driver.jid}"] .plate-popup`,
        )
        if (!popupElement || !platePopup) return

        const platePopupRect = platePopup.getElement()?.getBoundingClientRect()

        const xOffset = platePopupRect.width / 2 + 10

        // Create and show popup
        const popup = new Popup({
          closeButton: false,
          closeOnClick: false,
          offset: [-xOffset, -5],
          className: "car-popup",
          anchor: "top-left",
        })

        popup.setLngLat(coordinates)
        popup.setHTML(createDriverDetailHTML(driver))
        popup.addTo(mapInstance)

        // Track that this popup is now visible
        setVisiblePopupDriverJid(driver.jid)

        // Automatically show route when popup opens
        if (driver.hasRoute && driver.routeJid) {
          handleShowRoute(driver)
        }

        // Add drag functionality
        let isDragging = false
        let dragStartX = 0
        let dragStartY = 0
        let dragStartLngLat: [number, number] = [0, 0]

        const handleMouseDown = (e: MouseEvent) => {
          // Only allow dragging from the header area
          const target = e.target as HTMLElement
          const header = target.closest(".drag-handle")
          if (!header) return

          isDragging = true
          dragStartX = e.clientX
          dragStartY = e.clientY
          dragStartLngLat = [popup.getLngLat().lng, popup.getLngLat().lat]

          // Change cursor for entire document during drag
          document.body.style.cursor = "grabbing"

          e.preventDefault()
        }

        const handleMouseMove = (e: MouseEvent) => {
          if (!isDragging) return

          const dx = e.clientX - dragStartX
          const dy = e.clientY - dragStartY

          // Convert pixel movement to map coordinates
          const startPoint = mapInstance.project(dragStartLngLat)
          const newPoint = {
            x: startPoint.x + dx,
            y: startPoint.y + dy,
          }
          const newLngLat = mapInstance.unproject(newPoint as PointLike)

          popup.setLngLat([newLngLat.lng, newLngLat.lat])
        }

        const handleMouseUp = () => {
          if (isDragging) {
            isDragging = false
            document.body.style.cursor = ""
          }
        }

        const handleOpenChat = async (e: Event) => {
          e.stopPropagation()
          const chatJid = (e.currentTarget as HTMLElement)?.getAttribute(
            "data-chat-jid",
          )
          if (chatJid) {
            onNavigateToChat(chatJid, Boolean(driver.shouldJoin))
          }
        }

        const handleClose = (e: Event) => {
          e.stopPropagation()
          onDriverSelect?.(null)
          popup.remove()
          setVisiblePopupDriverJid(null)
          // Set the close time to prevent immediate interactions
          lastPopupCloseTimeRef.current = Date.now()
          // Also hide route when popup closes
          if (
            driver.routeJid &&
            visibleRouteDriverJidRef.current === driver.routeJid
          ) {
            removeRouteFromMap(driver.routeJid)
            visibleRouteDriverJidRef.current = null
          }
        }

        const handleFlyTo = (e: Event) => {
          e.stopPropagation()
          if (
            driver &&
            driver.location &&
            driver.location.lat &&
            driver.location.lon
          ) {
            mapManagerRef.current?.getMapInstance()?.flyTo({
              center: [
                parseFloat(driver.location.lon),
                parseFloat(driver.location.lat),
              ],
              zoom: 15,
              speed: 1.5,
            })
          }
        }

        // Store references to elements and their handlers for cleanup
        let chatButton: HTMLElement | null = null
        let closeButton: HTMLElement | null = null
        let flyButton: HTMLElement | null = null
        setTimeout(() => {
          const popupElement = popup.getElement()
          if (popupElement) {
            // Add drag handle class to header for styling
            const header = popupElement.querySelector(
              ".border-base.flex.items-center.justify-between.border-b.py-2.pl-3.pr-2",
            )
            if (header) {
              header.classList.add("drag-handle")
            }

            // Add drag event listeners to the popup element
            popupElement.addEventListener("mousedown", handleMouseDown)
            document.addEventListener("mousemove", handleMouseMove)
            document.addEventListener("mouseup", handleMouseUp)

            chatButton = popupElement.querySelector(
              `[data-chat-jid="${driver.chatJid || driver.jid}"]`,
            ) as HTMLElement | null

            if (chatButton) {
              chatButton.addEventListener("click", handleOpenChat)
            }

            closeButton = popupElement.querySelector(
              `[data-close-jid="${driver.chatJid || driver.jid}"]`,
            ) as HTMLElement | null

            if (closeButton) {
              closeButton.addEventListener("click", handleClose)
            }

            flyButton = popupElement.querySelector(
              `[data-fly-jid="${driver.chatJid || driver.jid}"]`,
            ) as HTMLElement | null

            if (flyButton) {
              flyButton.addEventListener("click", handleFlyTo)
            }

            popupElement.addEventListener("wheel", handleZoom)
          }
        }, 0)

        // Handle popup close event
        popup.on("close", () => {
          // Remove event listeners
          if (chatButton)
            chatButton.removeEventListener("click", handleOpenChat)
          if (closeButton) closeButton.removeEventListener("click", handleClose)
          if (flyButton) flyButton.removeEventListener("click", handleFlyTo)

          const popupElement = popup.getElement()
          if (popupElement) {
            popupElement.removeEventListener("mousedown", handleMouseDown)
            popupElement.removeEventListener("wheel", handleZoom)
          }
          document.removeEventListener("mousemove", handleMouseMove)
          document.removeEventListener("mouseup", handleMouseUp)

          // Reset cursor
          document.body.style.cursor = ""

          // Clear visible popup state but don't deselect driver
          setVisiblePopupDriverJid(null)
          delete popups.current[driver.jid]
        })

        // Store popup reference for cleanup
        popups.current[driver.jid] = popup
      },
      [
        createDriverDetailHTML,
        removeRouteFromMap,
        handleShowRoute,
        onNavigateToChat,
        onDriverSelect,
      ],
    )

    const handleFlyToDriver = useCallback(
      (driver: DriverDisplayInfo, zoomLevel?: number) => {
        if (
          driver &&
          driver.location &&
          driver.location.lat &&
          driver.location.lon
        ) {
          const mapInstance = mapManagerRef.current?.getMapInstance()
          if (mapInstance) {
            // Get current zoom level to maintain it
            const currentZoom = mapInstance.getZoom()

            mapInstance.flyTo({
              center: [
                parseFloat(driver.location.lon),
                parseFloat(driver.location.lat),
              ],
              zoom: zoomLevel ?? currentZoom,
              speed: 1.5,
            })
          }
        }
      },
      [],
    )

    // The manager keeps handing out its map after clearMap() (React strict
    // mode runs the effects of the second mount against the torn-down first
    // manager), and a removed map throws on getSource/addSource.
    const getLiveMap = useCallback(() => {
      const manager = mapManagerRef.current
      if (!manager || manager.isDestroyed()) return null
      return manager.getMapInstance()
    }, [])

    const openTrailerPopup = useCallback(
      (
        mapInstance: MapLibreMap,
        lngLat: [number, number],
        properties: TrailerPopupProperties,
      ) => {
        trailerPopupRef.current?.remove()
        trailerPopupRef.current = new Popup({
          closeButton: true,
          closeOnClick: true,
          offset: [0, -10],
          className: "trailer-detail-popup",
        })
          .setLngLat(lngLat)
          .setHTML(trailerPopupHTML(properties))
          .addTo(mapInstance)
      },
      [],
    )

    const trailerLngLat = (
      trailer: TrailerLocation,
    ): [number, number] | null =>
      trailer.location?.lat && trailer.location?.lon
        ? [parseFloat(trailer.location.lon), parseFloat(trailer.location.lat)]
        : null

    const flyToTrailer = useCallback(
      (trailer: TrailerLocation, zoomLevel?: number) => {
        const mapInstance = getLiveMap()
        const lngLat = trailerLngLat(trailer)
        if (!mapInstance || !lngLat) return
        mapInstance.flyTo({
          center: lngLat,
          zoom: zoomLevel ?? mapInstance.getZoom(),
          speed: 1.5,
        })
      },
      [getLiveMap],
    )

    // Picked from the list: open the popup where it is, or bring the trailer
    // into view first when it is off screen.
    const showTrailerPopup = useCallback(
      (trailer: TrailerLocation) => {
        const mapInstance = getLiveMap()
        const lngLat = trailerLngLat(trailer)
        if (!mapInstance || !lngLat) return
        const open = () =>
          openTrailerPopup(mapInstance, lngLat, {
            label: trailer.label,
            odometer: trailer.location?.odometer ?? null,
          })
        if (mapInstance.getBounds().contains(lngLat)) {
          open()
          return
        }
        mapInstance.once("moveend", open)
        mapInstance.flyTo({
          center: lngLat,
          zoom: Math.max(mapInstance.getZoom(), 9),
          speed: 1.5,
        })
      },
      [getLiveMap, openTrailerPopup],
    )

    // Expose showDriverDetailPopup through imperative handle
    useImperativeHandle(
      ref,
      () => ({
        showDriverPopup: showDriverDetailPopup,
        flyToDriver: handleFlyToDriver,
        showTrailerPopup,
        flyToTrailer,
      }),
      [
        handleFlyToDriver,
        showDriverDetailPopup,
        showTrailerPopup,
        flyToTrailer,
      ],
    )

    const bindTrailerHandlers = useCallback(() => {
      const mapInstance = getLiveMap()
      if (!mapInstance || trailerHandlersMapRef.current === mapInstance) return
      // Layer-scoped listeners survive the layer being removed and re-added
      // on the same instance, so bind them once per instance.
      trailerHandlersMapRef.current = mapInstance

      for (const layerId of TRAILER_HIT_LAYER_IDS) {
        mapInstance.on("mouseenter", layerId, () => {
          mapInstance.getCanvas().style.cursor = "pointer"
        })
        mapInstance.on("mouseleave", layerId, () => {
          mapInstance.getCanvas().style.cursor = ""
        })
        mapInstance.on("click", layerId, (event) => {
          const feature = event.features?.[0]
          if (!feature || feature.geometry.type !== "Point") return
          openTrailerPopup(
            mapInstance,
            feature.geometry.coordinates as [number, number],
            feature.properties as TrailerPopupProperties,
          )
        })
      }
    }, [getLiveMap, openTrailerPopup])

    const addTrailersLayer = useCallback(() => {
      const mapInstance = getLiveMap()
      if (!mapInstance) return

      for (const layerId of [
        TRAILERS_LABEL_LAYER_ID,
        TRAILERS_ARROW_LAYER_ID,
        TRAILERS_LAYER_ID,
      ]) {
        if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId)
      }
      if (mapInstance.getSource(TRAILERS_SOURCE_ID)) {
        mapInstance.removeSource(TRAILERS_SOURCE_ID)
      }
      if (!mapInstance.hasImage(TRAILER_ARROW_IMAGE_ID)) {
        mapInstance.addImage(TRAILER_ARROW_IMAGE_ID, trailerArrowImage(), {
          pixelRatio: 2,
        })
      }

      mapInstance.addSource(TRAILERS_SOURCE_ID, {
        type: "geojson",
        data: createTrailersGeoJSON(trailersRef.current),
      })
      mapInstance.addLayer({
        id: TRAILERS_LAYER_ID,
        type: "circle",
        source: TRAILERS_SOURCE_ID,
        filter: ["!", ["get", "hasHeading"]],
        paint: {
          "circle-radius": 6,
          "circle-color": "#f59e0b",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      })
      mapInstance.addLayer({
        id: TRAILERS_ARROW_LAYER_ID,
        type: "symbol",
        source: TRAILERS_SOURCE_ID,
        filter: ["get", "hasHeading"],
        layout: {
          "icon-image": TRAILER_ARROW_IMAGE_ID,
          "icon-rotate": ["get", "bearing"],
          "icon-rotation-alignment": "map",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      })
      mapInstance.addLayer({
        id: TRAILERS_LABEL_LAYER_ID,
        type: "symbol",
        source: TRAILERS_SOURCE_ID,
        minzoom: 7,
        layout: {
          "text-field": ["get", "label"],
          // Served by both the default (openfreemap) and satellite (MapTiler)
          // glyph endpoints; the label just stays hidden on styles without it.
          "text-font": ["Noto Sans Regular"],
          "text-size": 11,
          "text-anchor": "top",
          "text-offset": [0, 1],
          "text-optional": true,
        },
        paint: {
          "text-color": "#78350f",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.5,
        },
      })

      bindTrailerHandlers()
    }, [bindTrailerHandlers, getLiveMap])

    // The first style load can take longer than drawOnceMapIsLoaded's ~3.5 s
    // polling budget (the vehicle chips get away with it because the drivers
    // effect keeps retrying on every position update; trailers may not change
    // for a long time), so hang the layer on the style events instead.
    const ensureTrailersLayer = useCallback(() => {
      const mapInstance = getLiveMap()
      if (!mapInstance) return
      if (mapInstance.isStyleLoaded()) {
        addTrailersLayer()
        return
      }
      mapInstance.once("styledata", () => ensureTrailersLayer())
    }, [addTrailersLayer, getLiveMap])

    // Push trailer changes (new positions, the show/hide switch, search) into
    // the layer; build it first if the map got ready before the data did.
    // Not gated on isStyleLoaded(): that flips to false whenever tiles are
    // still loading, which would silently drop a toggle made mid-pan.
    useEffect(() => {
      const mapInstance = getLiveMap()
      if (!mapInstance) return

      const source = mapInstance.getSource(TRAILERS_SOURCE_ID) as
        | GeoJSONSource
        | undefined
      if (source) {
        source.setData(createTrailersGeoJSON(trailers))
      } else {
        ensureTrailersLayer()
      }
      if (trailers.length === 0) {
        trailerPopupRef.current?.remove()
        trailerPopupRef.current = null
      }
    }, [trailers, ensureTrailersLayer, getLiveMap])

    const addCarsLayer = useCallback(() => {
      const mapInstance = mapManagerRef.current?.getMapInstance()
      if (!mapInstance) return

      // Remove existing layers and source if they exist
      if (mapInstance.getLayer("cars-layer")) {
        mapInstance.removeLayer("cars-layer")
      }
      if (mapInstance.getSource("cars-source")) {
        mapInstance.removeSource("cars-source")
      }

      // Add the GeoJSON source with car data
      mapInstance.addSource("cars-source", {
        type: "geojson",
        data: createCarsGeoJSON(drivers),
      })

      // Create persistent popups after adding the layer
      createPersistentPopups()
    }, [drivers, createCarsGeoJSON, createPersistentPopups])

    // Update popup classes when hoveredDriver or selectedDriver changes
    useEffect(() => {
      updatePopupClasses()
    }, [hoveredDriver, selectedDriver, updatePopupClasses])

    // Update map data when drivers array changes (for filtering)
    useEffect(() => {
      const mapInstance = mapManagerRef.current?.getMapInstance()
      if (!mapInstance || !mapInstance.isStyleLoaded()) return

      // Update the cars source data with filtered drivers
      const source = mapInstance.getSource("cars-source") as GeoJSONSource
      if (source && source.type === "geojson") {
        source.setData(createCarsGeoJSON(drivers))
      }

      // Clean up existing persistent popups for drivers not in the filtered list
      Object.keys(persistentPopups.current).forEach((jid) => {
        if (!drivers.some((driver) => driver.jid === jid)) {
          cleanupPersistentPopup(jid)
        }
      })

      // Create persistent popups for new drivers
      createPersistentPopups()
    }, [
      drivers,
      createCarsGeoJSON,
      createPersistentPopups,
      cleanupPersistentPopup,
    ])

    const handleChangeMapStyle = useCallback(
      (style: MapStyle) => {
        if (currentMapStyle !== style) {
          // Clear existing popups before changing style
          Object.keys(persistentPopups.current).forEach((jid) => {
            cleanupPersistentPopup(jid)
          })

          // Clear any visible routes before changing style
          if (visibleRouteDriverJidRef.current) {
            removeRouteFromMap(visibleRouteDriverJidRef.current)
            visibleRouteDriverJidRef.current = null
          }

          trailerPopupRef.current?.remove()
          trailerPopupRef.current = null

          mapManagerRef.current?.changeMapStyle(style)
          setCurrentMapStyle(style)
          drawOnceMapIsLoaded(() => {
            addCarsLayer()
            createPersistentPopups()
          }, "cars-layer")
          ensureTrailersLayer()
        }
      },
      [
        addCarsLayer,
        ensureTrailersLayer,
        createPersistentPopups,
        cleanupPersistentPopup,
        currentMapStyle,
        drawOnceMapIsLoaded,
        removeRouteFromMap,
      ],
    )

    useMapToaster(mapContainerRef.current?.parentElement)

    useEffect(() => {
      if (mapContainerRef.current) {
        mapManagerRef.current = new MapManager({
          mapConfig: {
            style: "default",
            center: [20.0, 50.0],
            zoom: 3,
          },
          isDarkMode,
          mapContainer: mapContainerRef.current,
          currentLocation: null,
          isSharedMap: false,
          routingProvider,
        })

        ensureTrailersLayer()

        // Add a small delay to ensure map is fully initialized
        const timeoutId = setTimeout(() => {
          drawOnceMapIsLoaded(() => {
            addCarsLayer()
          }, "cars-layer")
        }, 100)

        timeoutsRef.current.add(timeoutId)
      }

      return () => {
        // Clean up persistent popups
        Object.keys(persistentPopups.current).forEach((jid) => {
          cleanupPersistentPopup(jid)
        })

        // Clean up any visible routes
        if (visibleRouteDriverJidRef.current) {
          removeRouteFromMap(visibleRouteDriverJidRef.current)
        }

        trailerPopupRef.current?.remove()
        trailerPopupRef.current = null

        mapManagerRef.current?.clearMap()
      }
    }, [])

    return (
      <>
        <div className="relative h-full w-full bg-white dark:bg-gray-800">
          <div
            className="h-full w-full"
            ref={mapContainerRef}
            id="mapContainer"
          ></div>
          <style>{`
          .car-popup {
            font-size: 14px;
            z-index: 999999;
          }
          .car-popup .maplibregl-popup-content {
            min-width: 400px;
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          .dark .car-popup .maplibregl-popup-content {
            background-color: #1f2937;
            color: white;
          }
          .trailer-detail-popup {
            z-index: 999998;
          }
          .trailer-detail-popup .maplibregl-popup-content {
            padding: 0;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.15);
          }
          .trailer-detail-popup .maplibregl-popup-close-button {
            padding: 0 6px;
            font-size: 16px;
            color: #6b7280;
          }
          .persistent-popup .maplibregl-popup-content {
            padding: 0;
            background: transparent;
            box-shadow: none;
            border: none;
          }
          .persistent-popup .maplibregl-popup-tip {
            display: none;
          }
          
          .maplibregl-popup-anchor-top-left .maplibregl-popup-tip {
            border: 0 !important;
          }

          .drag-handle {
            cursor: grab;
          }
          
          .drag-handle:active {
            cursor: grabbing;
          }

        `}</style>
        </div>
        <ChangeMapStyle
          currentStyle={currentMapStyle}
          onChangeMapStyle={handleChangeMapStyle}
        />
        <div
          style={{
            position: "absolute",
            top: "45px",
            left: "10px",
            zIndex: 10,
          }}
        >
          <Button
            id="show-entire-mape"
            onClick={() =>
              mapManagerRef.current?.getMapInstance().flyTo({
                center: [10, 55], // Approximate center of Europe
                zoom: 3.5, // Zoom level to show all of Europe
                duration: 1000, // Animation duration in milliseconds
              })
            }
            icon="globe"
            className="dark:bg-gray-700 dark:text-white"
            title={t("fleet.fleetOverview.showEntireMap")}
          />
        </div>
        {isMobile && <CloseMobileMap onClose={onClose} />}
      </>
    )
  },
)

FleetOverviewMap.displayName = "FleetOverviewMap"
export default FleetOverviewMap
